import { createContext, useContext, useEffect, useState } from "react";
import * as React from "react";
import {
  FieldValues,
  UseControllerProps,
  useController,
  useWatch
} from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper, { PaperProps } from "@mui/material/Paper";
import TextField from "@mui/material/TextField";

import { useDebounce } from "hooks";

import { ICD10Code, useGetICD10CodesQuery } from "services/icd10-codes";

import DraggableICD10Chip from "./draggable-icd10-chip.component";
import {
  isInactiveICD10Code,
  looksLikeIcd10Code
} from "./icd10-inactive.constants";
import InactiveICD10Replacements from "./inactive-icd10-replacements.component";

const AUDIOLOGY_ICD10_CODES_PREFIX = "H9";

// MUI compares PaperComponent by identity: a component created per render would
// remount the listbox on every keystroke. So the Paper is one module-level
// component, and the heading hint reaches it through context.
const TypedHeadingContext = createContext<ICD10Code | undefined>(undefined);

function ICD10ListboxPaper({ children, ...rest }: PaperProps) {
  const heading = useContext(TypedHeadingContext);
  return (
    <Paper {...rest}>
      {heading && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 0 }}
          data-testid={`icd10_search_heading_hint_${heading.code}`}
        >
          {heading.code} &ldquo;{heading.description}&rdquo; is no longer an
          active billable code, so it is not offered. Choose the specific code
          from the list below.
        </Alert>
      )}
      {children}
    </Paper>
  );
}

type ICDCodesFormFieldProps = {
  required?: boolean;
  label?: React.ReactNode;
  showOnlyActive?: boolean;
  disabled?: boolean;
  disabledText?: string;
  /**
   * Paint a selected code that is inactive red and show, under the field, the
   * billable codes to pick instead. On by default: an inactive code cannot be
   * saved, so every editor needs the way out. Pass false only for a picker
   * whose value is never saved against the active-code check.
   */
  highlightInactive?: boolean;
  onOptionChanged?: (value: ICD10Code[]) => void;
};

export type RhfICDCodesFormFieldProps<T extends FieldValues> =
  ICDCodesFormFieldProps & UseControllerProps<T>;

export default function ICDCodesFormField<T extends FieldValues>({
  control,
  name,
  label = "ICD-10 Codes",
  required = false,
  rules,
  showOnlyActive = true,
  disabled = false,
  disabledText,
  highlightInactive = true,
  onOptionChanged
}: RhfICDCodesFormFieldProps<T>) {
  const [search, setSearch] = useState<string>("");
  const [options, setOptions] = useState<ICD10Code[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const { value: dSearch, isDebouncing } = useDebounce(
    search || AUDIOLOGY_ICD10_CODES_PREFIX
  );

  const { data: icdCodes, isLoading: isSearching } = useGetICD10CodesQuery({
    search: dSearch,
    is_active: showOnlyActive ? true : undefined
  });

  // Someone who has always typed "H61.2" no longer sees that row: it is a
  // heading, inactive, and the search is active-only. The real codes beneath it
  // (H61.20..H61.23) still match the same text, so they are in the list; this
  // looks up the exact code typed and, when it is an inactive heading, says so
  // at the top of the dropdown instead of leaving the row silently missing.
  // Debounced like the search itself, so it is one small request per pause, not per keystroke.
  const typedText = search.trim() ? dSearch.trim().toUpperCase() : "";
  const typedCode = looksLikeIcd10Code(typedText) ? typedText : "";
  const { data: typedCodeRows } = useGetICD10CodesQuery(
    { code: typedCode, is_active: false, limit: 1 },
    { skip: !showOnlyActive || !typedCode }
  );
  // `skip` keeps the last answer, so only trust a row that matches what is typed now.
  const headingTyped =
    typedCode && typedCodeRows?.[0]?.code === typedCode
      ? typedCodeRows[0]
      : undefined;

  const {
    fieldState: { error },
    field: { onChange, onBlur, ...restField }
  } = useController<T>({
    name,
    control,
    rules: {
      required: {
        value: required,
        message: `${label || "Value"} is required`
      },
      ...rules
    }
  });

  const errorMessage = error?.message;

  const selectedICD10Codes = useWatch({ control, name });
  const disabledHelperText = disabled ? disabledText : "";
  const renderInput = React.useCallback(
    (params: any) => (
      <TextField
        {...params}
        error={Boolean(errorMessage || disabled)}
        helperText={errorMessage || disabledHelperText}
        required={required}
        disabled={disabled}
        InputLabelProps={{ disabled: false }}
        label={label}
        autoFocus={open && !disabled}
      />
    ),
    [label, open, required, errorMessage, disabled, disabledHelperText]
  );

  useEffect(() => {
    const icd10Codes = icdCodes || [];
    return setOptions([...(selectedICD10Codes ?? []), ...icd10Codes]);
  }, [search, icdCodes, selectedICD10Codes]);

  const selectedCodes: ICD10Code[] = selectedICD10Codes ?? [];
  const inactiveSelectedCodes =
    highlightInactive && !disabled
      ? selectedCodes.filter(isInactiveICD10Code)
      : [];

  // Swap an inactive code for the pick, in the same position so the diagnosis
  // order (A, B, C... on the claim) is kept. With no pick the code is removed.
  // A pick already in the list is not added twice.
  const replaceInactiveCode = (
    inactive: ICD10Code,
    replacement: ICD10Code | null
  ) => {
    const alreadySelected = Boolean(
      replacement && selectedCodes.some((code) => code.id === replacement.id)
    );
    const next = selectedCodes.flatMap((code) => {
      if (code.id !== inactive.id) return [code];
      return replacement && !alreadySelected ? [replacement] : [];
    });
    // The options list is rebuilt from the value in an effect, one render
    // late; add the pick now so Autocomplete never sees a value it cannot match.
    setOptions((previous) => [
      ...next.filter((code) => !previous.some((o) => o.id === code.id)),
      ...previous
    ]);
    onOptionChanged?.(next);
    onChange(next);
    onBlur();
  };

  return (
    <Box width="100%">
      <TypedHeadingContext.Provider value={headingTyped}>
        <Autocomplete
          disablePortal
          loading={isSearching || isDebouncing}
          fullWidth
          filterSelectedOptions
          disabled={disabled}
          onOpen={() => !disabled && setOpen(true)}
          onClose={() => setOpen(false)}
          open={open && !disabled}
          options={options}
          getOptionLabel={(option) =>
            typeof option === "string"
              ? option
              : `${option.code}: ${option?.description}`
          }
          renderOption={(props: object, option: ICD10Code) => (
            <ListItem {...props}>
              <ListItemText secondary={option.code}>
                {option.description}
              </ListItemText>
            </ListItem>
          )}
          isOptionEqualToValue={(option: ICD10Code, value: ICD10Code) =>
            option.id === value.id
          }
          multiple
          disableCloseOnSelect
          inputValue={search}
          onInputChange={(event, newInputValue, reason) => {
            if (Boolean(event) && !disabled) {
              if (reason === "input") {
                setSearch(newInputValue);
              } else {
                setSearch("");
              }
            }
          }}
          renderTags={(value, getTagProps) => {
            const moveChip = (dragIndex: number, hoverIndex: number) => {
              if (disabled) return;
              const reordered = [...value];
              const [moved] = reordered.splice(dragIndex, 1);
              reordered.splice(hoverIndex, 0, moved);
              onOptionChanged?.(reordered);
              onChange(reordered);
            };

            return value.map((option, index) => {
              const { key: _key, ...tagProps } = getTagProps({ index });

              return (
                <DraggableICD10Chip
                  key={option.id}
                  option={option}
                  index={index}
                  total={value.length}
                  disabled={disabled}
                  tagProps={tagProps}
                  moveChip={moveChip}
                  highlightInactive={highlightInactive}
                />
              );
            });
          }}
          filterOptions={(x) => x}
          PaperComponent={ICD10ListboxPaper}
          popupIcon={<ArrowDropDownIcon />}
          renderInput={renderInput}
          onChange={(_, data) => {
            if (!disabled) {
              onOptionChanged?.(data);
              onChange(data);
              onBlur();
            }
          }}
          {...restField}
        />
      </TypedHeadingContext.Provider>
      {inactiveSelectedCodes.map((code) => (
        <InactiveICD10Replacements
          key={code.id}
          code={code}
          onReplace={(replacement) => replaceInactiveCode(code, replacement)}
          onRemove={() => replaceInactiveCode(code, null)}
        />
      ))}
    </Box>
  );
}
